<script setup lang="ts">
import { computed } from 'vue'
interface Props {
  name?: string
  party: string
  constituencyWon?: number
  constituencyVotes?: number
  imageUrl?: string
  color?: string
}

const props = defineProps<Props>()

const cardBackgroundStyle = computed(() => ({
  backgroundColor: `${props.color}4D`
}))
const nameStyle = computed(() => ({
  color: `${props.color}`
}))
</script>

<template>
  <div class="electoral-card" :style="cardBackgroundStyle">
    <div class="image-container">
      <img :src="imageUrl" :alt="`Photo of ${name}`" class="candidate-image" />
    </div>
    <div class="content">
      <h2 class="name" :style="nameStyle">{{ name }}</h2>

      <div class="votes-container">
        <div class="vote-section" v-if="constituencyWon !== undefined">
          <div class="vote-number">{{ constituencyWon }}</div>
          <div class="vote-label">Constituency</div>
        </div>

        <div class="vote-section">
          <div class="vote-number">{{ constituencyVotes ? constituencyVotes.toLocaleString() : '' }}</div>
          <div class="vote-label">Votes</div>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
.electoral-card {
  display: flex;
  align-items: stretch;
  border-radius: 0.75rem;
  padding: 0;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  width: 100%;
  margin: 0.5rem 0.1rem;
  overflow: hidden;
  position: relative;
  height: 130px;
}

.electoral-card .content {
  flex-grow: 1;
  padding: 1rem;
  max-width: calc(100% - 120px);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-end;
  text-align: right;
}

.electoral-card .name {
  font-size: 2rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  line-height: 1.2;
}

.electoral-card .votes-container {
  display: flex;
  gap: 1.8rem;
  justify-content: flex-end;
  align-items: center;
}

.electoral-card .vote-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
}

.electoral-card .vote-number {
  font-size: 2.6rem;
  font-weight: 700;
  line-height: 1;
  margin-bottom: 0.25rem;
  text-align: right;
}

.electoral-card .vote-label {
  color: #4b5563;
  font-size: 0.9rem;
  text-align: right;
}

.electoral-card .image-container {
  position: relative;
  width: 120px;
  height: 130px;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #f8f9fa;
}

.electoral-card .candidate-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

/* Responsive styles */
@media (max-width: 768px) {
  .electoral-card {
    flex-direction: column;
    height: auto;
  }

  .electoral-card .content {
    max-width: 100%;
    padding: 1rem;
    align-items: flex-start;
    text-align: left;
  }

  .electoral-card .votes-container {
    flex-direction: column;
    gap: 0.5rem;
    align-items: flex-start;
    justify-content: flex-start;
  }

  .electoral-card .vote-section {
    align-items: flex-start;
  }

  .electoral-card .vote-number {
    font-size: 1.75rem;
    text-align: left;
  }

  .electoral-card .vote-label {
    text-align: left;
  }

  .electoral-card .name {
    font-size: 1.25rem;
  }
}

/* Hover effects */
.electoral-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.electoral-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
}

/* Optional animation for numbers */
@keyframes countUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.electoral-card .vote-number {
  animation: countUp 0.5s ease-out forwards;
}
</style>